import { Getter, Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { IScenarioDataModel } from '../../../../data-models/scenario.data-model';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import {
  createScenario,
  deleteScenario,
  updateScenario,
} from '../../../../services/queries/MaggieScenarioQueries';
import { primaryCaptableStateJ } from '../../../../services/state/CapTableDataStateJ';
import {
  currentScenarioStateJ,
  scenariosByCompanyIdStateJ,
} from '../../../../services/state/ScenariosStateJ';
import { getErrorMessage } from '../../../../services/queryHelpers';
import { useLoadingBarState } from '../../../../components/LoadingBar/LoadingBarContext';
import { LoadingId } from '../../../../types';
import { ROUTES } from '../../../../constants/RouteNameMapping';

import { selectedCompanyIdProfileAtom } from '../../state/UIStateJ';
import { getForesightStore } from '../../../../util/jotai-store';
import { useAllScenariosLocation } from './useAllScenariosLocation';

export const ProgrammaticNavigationFlag = 'programmatic';

export function useScenarioActions() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const { actions } = useLoadingBarState();

  const navigate = useNavigate();
  const allScenariosLocation = useAllScenariosLocation();
  const { search: searchParams } = useLocation();
  const [loading, setLoading] = useState(false);

  const goToAllScenarios = useCallback(() => {
    if (!allScenariosLocation) {
      return;
    }
    navigate(allScenariosLocation, { state: { [ProgrammaticNavigationFlag]: true } });
  }, [allScenariosLocation, navigate]);

  const handleCancel = useCallback(() => {
    goToAllScenarios();
  }, [goToAllScenarios]);

  /**  preserves search params */
  const gotoSettings = useCallback(() => {
    navigate({
      pathname: `${allScenariosLocation}/${ROUTES.COMPANY_PROFILE_SINGLE_SCENARIO}/${ROUTES.COMPANY_PROFILE_SCENARIO_SETTINGS}`,
      search: searchParams,
    });
  }, [allScenariosLocation, navigate, searchParams]);

  /**  preserves search params */
  const gotoVisualization = useCallback(() => {
    navigate({
      pathname: `${allScenariosLocation}/${ROUTES.COMPANY_PROFILE_SINGLE_SCENARIO}/${ROUTES.COMPANY_PROFILE_SCENARIO_RUN}`,
      search: searchParams,
    });
  }, [navigate, allScenariosLocation, searchParams]);

  const handleUpdateScenario = useAtomCallback(
    useCallback(
      async (get: Getter, set: Setter, id: number, payload: Partial<IScenarioDataModel>) => {
        try {
          setLoading(true);
          actions.startLoading(LoadingId.updateScenario);
          const data = await updateScenario(id, payload);

          const { companyId } = data;
          const current = await get(scenariosByCompanyIdStateJ(companyId));
          if (current) {
            set(
              scenariosByCompanyIdStateJ(companyId),
              current.map((scenario) => (scenario.id === data.id ? data : scenario))
            );
          }
          pushSuccessToast({ message: 'Scenario updated successfully' });
          // if we are not going back to all scenarios, we need to set current scenario to data
          goToAllScenarios();
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Error updating scenario');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.updateScenario);
          setLoading(false);
        }
      },
      [actions, goToAllScenarios, pushErrorToast, pushSuccessToast]
    )
  );

  const handleCreateScenario = useAtomCallback(
    useCallback(
      async (get: Getter, set: Setter, payload: Partial<IScenarioDataModel>) => {
        actions.startLoading(LoadingId.createScenario);
        try {
          setLoading(true);
          const data = await createScenario(payload);
          const { companyId } = data;
          const current = await get(scenariosByCompanyIdStateJ(companyId));
          if (current) {
            set(scenariosByCompanyIdStateJ(companyId), [...current, data]);
          }
          // if we are not going back to all scenarios, we need to set current scenario to data
          goToAllScenarios();
          pushSuccessToast({ message: 'Scenario created successfully' });
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Error creating scenario');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.createScenario);
          setLoading(false);
        }
      },
      [actions, goToAllScenarios, pushErrorToast, pushSuccessToast]
    )
  );

  const handleDeleteScenario = useAtomCallback(
    useCallback(
      async (get: Getter, set: Setter, id: number) => {
        actions.startLoading(LoadingId.deleteScenario);
        try {
          setLoading(true);
          const companyId = getForesightStore().get(selectedCompanyIdProfileAtom);
          await deleteScenario(id);
          const current = await get(scenariosByCompanyIdStateJ(companyId));
          if (current) {
            set(
              scenariosByCompanyIdStateJ(companyId),
              current.filter((scenario) => scenario.id !== id)
            );
          }
          pushSuccessToast({ message: 'Scenario deleted successfully' });
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Error deleting scenario');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.deleteScenario);
          setLoading(false);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );

  const handleUpdateScenarioCaptable = useAtomCallback(
    useCallback(
      async (get: Getter, set: Setter, id: number, companyId: number) => {
        const primaryCaptable = await get(primaryCaptableStateJ(companyId));
        try {
          if (!primaryCaptable) {
            throw new Error('No primary captable found');
          }
          setLoading(true);
          actions.startLoading(LoadingId.updateScenario);
          const data = await updateScenario(id, { captableId: primaryCaptable.id });

          const current = await get(scenariosByCompanyIdStateJ(companyId));
          if (current) {
            set(
              scenariosByCompanyIdStateJ(companyId),
              current.map((scenario) => (scenario.id === data.id ? data : scenario))
            );
          }
          pushSuccessToast({ message: 'Captable updated successfully' });
          const curr = get(currentScenarioStateJ);
          if (curr) {
            set(currentScenarioStateJ, {
              ...curr,
              captableId: data.captableId,
              outdated: data.outdated,
            });
          }
          return data;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Error updating captable');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.updateScenario);
          setLoading(false);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );

  const handleRunScenario = useCallback(async () => {
    gotoVisualization();
  }, [gotoVisualization]);

  /** Navigates to the Weighted Outcome drill-in (same pattern as the single-scenario run view). */
  const gotoWeightedOutcome = useCallback(() => {
    navigate({
      pathname: `${allScenariosLocation}/${ROUTES.COMPANY_PROFILE_SINGLE_SCENARIO}/${ROUTES.COMPANY_PROFILE_SCENARIO_WEIGHTED}`,
    });
  }, [allScenariosLocation, navigate]);

  return {
    handleUpdateScenario,
    handleCreateScenario,
    handleDeleteScenario,
    handleRunScenario,
    handleUpdateScenarioCaptable,
    loading,
    handleCancel,
    gotoVisualization,
    gotoWeightedOutcome,
    gotoSettings,
  };
}
